import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { MenuModule } from 'primeng/menu';
import { ChartModule } from 'primeng/chart';
import { ChartData, ChartOptions } from 'chart.js';
import { TabsModule } from 'primeng/tabs';
import { SelectModule } from 'primeng/select';
import { FormControl, FormsModule } from '@angular/forms';
import { DynamicColumn } from '../../@shared/components/table-component/table-component';
import { RideService } from '../../services/rides/ride-service';
import { PaginatorState } from 'primeng/paginator';
import { Ride } from '../../models/ride';
import { distinctUntilChanged, map, switchMap, tap } from 'rxjs';
import { Params } from '../../services/utilis/add-params-service';
import { DashboardService } from '../../services/dashboard/dashboard-service';
import { CarStatistics, CarStatisticsResponse } from '../../models/car-statistics';
import { RideStatistics } from '../../models/ride-statistics';
import { CustomerStatistics } from '../../models/customer-statistics';
import { RevenueStatistics } from '../../models/revenue-statistics';
import { RevenueChartResponse } from '../../models/revenue-chart';
import { RideChartResponse } from '../../models/ride-chart';

@Component({
  selector: 'app-dashboard',
  imports: [CommonModule, FormsModule, ButtonModule, CardModule, MenuModule, ChartModule, SelectModule, TabsModule],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.scss'
})
export class Dashboard implements OnInit, OnDestroy {
  today: Date = new Date();
  params = [
    "page",
    "size",
    "search",
    'sortBy',
    'sortDirection',
    'startedFrom',
    'startedTo',
    'status',
    'minRating',
    'maxRating',
    'photoStatus',
  ]

  modifiedParams: Params = {
    page: "page",
    size: "size",
    search: "search",
    sortBy: "sortBy",
    sortDirection: "sortDirection",
    startedFrom: "startedFrom",
    startedTo: "startedTo",
    status: "status",
    minRating: "minRating",
    maxRating: "maxRating",
    photoStatus: "photoStatus",
  }

  // Define columns to show in the table
  tableColumns: DynamicColumn<Ride>[] = [
    { header: 'Ride ID', field: 'rideId', sortable: false, },
    { header: 'Started At', field: 'startedAt', sortable: true, isDateTime: true },
    { header: 'Plate Number', field: 'plateNo', sortable: false },
    { header: 'Distance (KM)', field: 'distanceKm', sortable: false },
    { header: 'Duration', field: 'durationSeconds', sortable: false, isDuration: true },
    { header: 'Customer Name', field: 'customerFirstName', mergedName: "customerLastName", sortable: true },
    {
      header: 'customer Email',
      field: 'customerEmail',
      sortable: true,
      command: (report) => this.redirectToCustomersPage(report),
      cellClass: 'text-primary underline cursor-pointer'
    },
    { header: 'customer Number', field: 'customerPhoneNumber', sortable: true },
  ];

  selectedPeriod = 'TODAY';

  activeRides = signal<Ride[]>([]);
  totalRecords = signal(0);
  first = 0;
  rows = 20;

  periodOptions = [
    { label: 'Today', value: 'TODAY' },
    { label: 'This Week', value: 'THIS_WEEK' },
    { label: 'This Month', value: 'THIS_MONTH' }
  ]

  carStatistics: CarStatistics
  rideStatistics: RideStatistics
  customerStatistics: CustomerStatistics
  revenueStatistics: RevenueStatistics
  revenueAreaChart: RevenueChartResponse
  rideAreaChart: RideChartResponse
  rideChartData: ChartData<'bar'> = { labels: [], datasets: [] };
  revenueChartData: ChartData<'bar'> = { labels: [], datasets: [] };
  rideChartOptions: ChartOptions<'bar'> = this.buildBarChartOptions(true);
  revenueChartOptions: ChartOptions<'bar'> = this.buildRevenueChartOptions(true);

  searchControl = new FormControl('');

  selectedTab: 'carSharing' | 'carRental' = 'carSharing';

  readonly statusConfig: { key: keyof CarStatisticsResponse; label: string; colorKey: string; dotColor?: string; statusValue?: string; noClick?: boolean; customParams?: Record<string, string> }[] = [
    { key: 'available', label: 'Available', colorKey: 'available', dotColor: '#15803D' },
    { key: 'inUse', label: 'In Use', colorKey: 'in-use' },
    { key: 'reserved', label: 'Reserved', colorKey: 'reserved' },
    { key: 'opsInUse', label: 'OPS Use', colorKey: 'ops-use' },
    { key: 'discharged', label: 'Discharged', colorKey: 'discharged' },
    { key: 'maintenance', label: 'Maintenance', colorKey: 'maintenance' },
    { key: 'rebalancing', label: 'Rebalancing', colorKey: 'rebalancing' },
    { key: 'needsRebalancing', label: 'Need Rebalance', colorKey: 'need-rebalance', customParams: { needsRebalancing: 'true' } },
    { key: 'needsInvestigation', label: 'Investigate', colorKey: 'investigate' },
    { key: 'transportation', label: 'Transportation', colorKey: 'transportation' },
    { key: 'internalRent', label: 'Internal Rent', colorKey: 'internal-rent', dotColor: '#1F2937' },
    { key: 'needsRenewal', label: 'Need Renewal', colorKey: 'not-ready', statusValue: 'NEEDS_RENEWAL' },
    { key: 'cleaning', label: 'Cleaning', colorKey: 'cleaning' },
    { key: 'needsCleaning', label: 'Need cleaning', colorKey: 'need-cleaning', dotColor: '#9CA3AF' },
    { key: 'stolen', label: 'Stolen', colorKey: 'stolen' },
    { key: 'totalLoss', label: 'Total Loss', colorKey: 'total-loss', dotColor: '#7F1D1D' },
  ];

  get statusItems() {
    const stats = this.selectedCarStatistics;

    return this.statusConfig.map(cfg => ({
      label: cfg.label,
      value: stats[cfg.key] ?? 0,
      dotColor: cfg.dotColor ?? `var(--status-${cfg.colorKey}-text)`,
      textColor: `var(--status-${cfg.colorKey}-text)`,
      statusKey: cfg.statusValue ?? cfg.key.replace(/([A-Z])/g, '_$1').toUpperCase(),
      noClick: cfg.noClick ?? false,
      customParams: cfg.customParams ?? null,
    }));
  }

  navigateToVehiclesByStatus(statusKey: string, customParams?: Record<string, string> | null, event?: MouseEvent): void {
    if (event?.ctrlKey || event?.metaKey) {
      const params = customParams ?? { status: statusKey, rentalType: this.selectedTab === 'carRental' ? 'CAR_RENTAL' : 'CAR_SHARING' };
      window.open('/vehicles?' + new URLSearchParams(params).toString(), '_blank');
      return;
    }
    if (customParams) {
      this.router.navigate(['/vehicles'], { queryParams: customParams });
      return;
    }
    const rentalTypeMap: Record<string, string> = {
      carSharing: 'CAR_SHARING',
      carRental: 'CAR_RENTAL',
    };

    this.router.navigate(['/vehicles'], {
      queryParams: {
        status: statusKey,
        rentalType: rentalTypeMap[this.selectedTab],
      },
    });
  }

  private rideService = inject(RideService)
  private dashboardService = inject(DashboardService)
  private activatedRoute = inject(ActivatedRoute)
  private router = inject(Router)

  ngOnInit() {
    this.activatedRoute.queryParams.subscribe(params => {

      const tab = params['tab'];

      if (tab === 'carSharing' || tab === 'carRental') {
        this.selectedTab = tab;
      }

      document.body.setAttribute('data-dashboard-tab', this.selectedTab);
    });
    // this.getActiveRides()
    this.getCarStatistics()
    this.getRideStatistics()
    this.getRevenueStatistics()
    this.getCustomerStatistics()
    this.getRevenueAreaChart()
    this.getRideAreaChart()
  }

  getActiveRides = () => {
    this.activatedRoute.queryParams.pipe(
      map((params) =>
        Object.fromEntries(
          Object.entries(params)
            .filter(([key]) => this.params.includes(key))
            .map(([key, value]) => [this.modifiedParams[key] || key, value])
        )
      ),

      distinctUntilChanged((previous, current) =>
        JSON.stringify(previous) === JSON.stringify(current)
      ),

      tap((queryParams) => {
        this.searchControl.setValue(queryParams['search'], { emitEvent: false });
        this.rows = queryParams['size'] ? +queryParams['size'] : 20;
      }),
      switchMap((queryParams) => {
        const params = {
          ...queryParams,
          status: queryParams['status'] ?? 'ACTIVE',
          sortBy: queryParams['sortBy'] ?? 'startedAt',
          sortDirection: queryParams['sortDirection'] ?? 'DESC',
        };
        return this.rideService.getRidesList(params);
      })
    )
      .subscribe((res) => {
        this.activeRides.set(res.data.content);
        this.totalRecords.set(res.data.totalElements)
        this.first = ((res.data.currentPage + 1) * res.data.pageSize) - res.data.pageSize
      });
  }

  getCarStatistics = () => {
    this.dashboardService.getCarStatistics().subscribe({
      next: (res) => {
        this.carStatistics = res.data
      }
    })
  }

  getRideStatistics = () => {
    this.dashboardService.getRideStatistics(this.statisticsParams).subscribe({
      next: (res) => {
        this.rideStatistics = res.data
      }
    })
  }

  getRevenueStatistics = () => {
    this.dashboardService.getRevenueStatistics(this.statisticsParams).subscribe({
      next: (res) => {
        this.revenueStatistics = res.data
      }
    })
  }

  getCustomerStatistics = () => {
    this.dashboardService.getCustomerStatistics(this.statisticsParams).subscribe({
      next: (res) => {
        this.customerStatistics = res
      }
    })
  }

  get selectedCarStatistics(): CarStatisticsResponse {
    if (!this.carStatistics) return {} as CarStatisticsResponse;

    return this.carStatistics[this.selectedTab];
  }

  get selectedRideStatistics(): number {
    if (!this.rideStatistics) return 0;

    return this.rideStatistics[this.selectedTab];
  }

  get selectedRevenueStatistics(): number {
    if (!this.revenueStatistics) return 0;

    return this.revenueStatistics[this.selectedTab];
  }

  get totalCustomers(): number {
    return this.customerStatistics?.data ?? 0;
  }

  get statisticsParams(): Params {
    return { period: this.selectedPeriod };
  }

  get selectedPeriodLabel(): string {
    const map: Record<string, string> = {
      TODAY: 'Today', THIS_WEEK: 'This Week', THIS_MONTH: 'This Month',
    };
    return map[this.selectedPeriod] ?? 'Today';
  }

  get periodPrefix(): string {
    const map: Record<string, string> = {
      TODAY: "Today's", THIS_WEEK: "Week's", THIS_MONTH: "Month's",
    };
    return map[this.selectedPeriod] ?? "Today's";
  }

  private buildLabels(src: { time: string }[]): string[] {
    return src.map((d, i) =>
      this.selectedPeriod === 'TODAY' ? (i % 3 === 0 ? this.formatChartLabel(d.time) : '') : this.formatChartLabel(d.time)
    );
  }

  private buildBarChartOptions(isToday: boolean): ChartOptions<'bar'> {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        legend: { display: false },
        tooltip: { mode: 'index', intersect: false },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { font: { size: 10 }, maxRotation: 0, autoSkip: !isToday, autoSkipPadding: 8 },
        },
        y: { grid: { color: '#E5E7EB' }, beginAtZero: true, ticks: { precision: 0 } },
      },
    };
  }

  private buildRevenueChartOptions(isToday: boolean): ChartOptions<'bar'> {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: { label: (ctx) => ` ${ctx.formattedValue} AED` },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { font: { size: 10 }, maxRotation: 0, autoSkip: !isToday, autoSkipPadding: 8 },
        },
        y: { grid: { color: '#E5E7EB' }, beginAtZero: true, ticks: { precision: 0 } },
      },
    };
  }

  private updateRideChartData(): void {
    if (!this.rideAreaChart) return;
    const src = this.selectedTab === 'carSharing' ? this.rideAreaChart.carSharing : this.rideAreaChart.carRental;
    const counts = src.map(d => d.count);
    const maxVal = Math.max(...counts, 0);
    this.rideChartData = {
      labels: this.buildLabels(src),
      datasets: [{
        data: counts,
        backgroundColor: counts.map(c => c === maxVal && maxVal > 0
          ? (this.selectedTab === 'carRental' ? '#2A67B1' : '#49A168')
          : '#E2E8F0'),
        borderRadius: 4,
        borderSkipped: false,
      }],
    };
  }

  private updateRevenueChartData(): void {
    if (!this.revenueAreaChart) return;
    const src = this.selectedTab === 'carSharing' ? this.revenueAreaChart.carSharing : this.revenueAreaChart.carRental;
    const amounts = src.map(d => d.amount);
    const maxVal = Math.max(...amounts, 0);
    this.revenueChartData = {
      labels: this.buildLabels(src),
      datasets: [{
        data: amounts,
        backgroundColor: amounts.map(a => a === maxVal && maxVal > 0 ? '#334155' : '#E2E8F0'),
        borderRadius: 4,
        borderSkipped: false,
      }],
    };
  }

  private updateChartOptions(): void {
    const isToday = this.selectedPeriod === 'TODAY';
    this.rideChartOptions = this.buildBarChartOptions(isToday);
    this.revenueChartOptions = this.buildRevenueChartOptions(isToday);
  }

  get peakLabel(): string {
    return this.selectedPeriod === 'TODAY' ? 'PEAK HOUR' : 'PEAK DAY';
  }

  get ridesPeakInfo(): { label: string; count: number } {
    if (!this.rideAreaChart) return { label: '—', count: 0 };
    const src = this.selectedTab === 'carSharing' ? this.rideAreaChart.carSharing : this.rideAreaChart.carRental;
    if (!src.length) return { label: '—', count: 0 };
    const max = src.reduce((a, b) => a.count > b.count ? a : b);
    if (max.count == 0) { return { label: '-', count: 0 } }
    return { label: this.peakTimeLabel(max.time), count: max.count };
  }

  get revenuePeakInfo(): { label: string } {
    if (!this.revenueAreaChart) return { label: '—' };
    const src = this.selectedTab === 'carSharing' ? this.revenueAreaChart.carSharing : this.revenueAreaChart.carRental;
    if (!src.length) return { label: '—' };
    const max = src.reduce((a, b) => a.amount > b.amount ? a : b);
    if (max.amount == 0) { return { label: '-' } }
    return { label: this.peakTimeLabel(max.time) };
  }

  private peakTimeLabel(time: string): string {
    const d = this.getDate(time);
    if (this.selectedPeriod === 'TODAY') {
      const h = d.getHours();
      return `${h.toString().padStart(2, '0')}:00 – ${(h + 1).toString().padStart(2, '0')}:00`;
    }
    if (this.selectedPeriod === 'THIS_WEEK') {
      return d.toLocaleDateString([], { weekday: 'long' });
    }
    return d.toLocaleDateString([], { day: 'numeric', month: 'short' });
  }

  navigateToCustomers(filterByToday = false, event?: MouseEvent): void {
    if (event?.ctrlKey || event?.metaKey) {
      window.open('/customers', '_blank');
      return;
    }
    const queryParams: Record<string, string> = {};

    if (filterByToday) {
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const end = new Date();
      end.setHours(23, 59, 59, 999);
      queryParams['createdFrom'] = start.toISOString();
      queryParams['createdTo'] = end.toISOString();
    }

    this.router.navigate(['/customers'], { queryParams });
  }

  navigateToRides(filterByToday = false, event?: MouseEvent): void {
    const tab = this.selectedTab === 'carRental' ? 'RIDE_RENTAL' : 'RIDE_SHARING';
    if (event?.ctrlKey || event?.metaKey) {
      window.open(`/rides?tab=${tab}`, '_blank');
      return;
    }
    const queryParams: Record<string, string> = { tab };

    if (filterByToday) {
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const end = new Date();
      end.setHours(23, 59, 59, 999);
      queryParams['startedFrom'] = start.toISOString();
      queryParams['startedTo'] = end.toISOString();
    }

    this.router.navigate(['/rides'], { queryParams });
  }

  onPageChange = (event: PaginatorState) => {
    const queryparams = {
      page: event.page,
      size: event.rows,
    }
    this.router.navigate([], {
      queryParams: queryparams,
      queryParamsHandling: 'merge'
    })
  }

  ngOnDestroy() {
    document.body.removeAttribute('data-dashboard-tab');
  }

  onTabChange = (tab: string | number | undefined) => {
    if (tab !== 'carSharing' && tab !== 'carRental') return;

    this.selectedTab = tab;
    document.body.setAttribute('data-dashboard-tab', tab);
    this.updateRideChartData();
    this.updateRevenueChartData();

    this.router.navigate([], {
      queryParams: { tab },
      queryParamsHandling: 'merge'
    });
  }

  onPeriodChange = () => {
    this.updateChartOptions();
    this.getRideStatistics();
    this.getRevenueStatistics();
    this.getCustomerStatistics();
    this.getRevenueAreaChart();
    this.getRideAreaChart();
  }

  get revenueAreaChartData(): ChartData<'line'> {

    if (!this.revenueAreaChart) {
      return {
        labels: [],
        datasets: [],
      };
    }

    const selectedData =
      this.selectedTab === 'carSharing'
        ? this.revenueAreaChart.carSharing
        : this.revenueAreaChart.carRental;

    return {
      labels: selectedData.map(item =>
        this.formatChartLabel(item.time)
      ),

      datasets: [
        {
          label: 'Revenue',
          data: selectedData.map(item => item.amount),
          borderColor: '#3B82F6',
          backgroundColor: 'rgba(59,130,246,0.1)',
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#3B82F6',
        },
      ],
    };
  }

  getRevenueAreaChart = () => {
    this.dashboardService
      .getRevenueAreaChart(this.statisticsParams)
      .subscribe({
        next: (res) => {
          this.revenueAreaChart = res.data;
          this.updateRevenueChartData();
        }
      });
  }

  revenueAreaChartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => `Earnings: ${context.formattedValue} AED`,
        },
      },
    },
    scales: {
      x: { grid: { display: false } },
      y: {
        grid: { color: '#E5E7EB' },
        beginAtZero: true,
        ticks: {
          precision: 0
        }
      },
    },
  };

  get rideAreaChartData(): ChartData<'line'> {

    if (!this.rideAreaChart) {
      return {
        labels: [],
        datasets: [],
      };
    }

    const selectedData =
      this.selectedTab === 'carSharing'
        ? this.rideAreaChart.carSharing
        : this.rideAreaChart.carRental;

    return {
      labels: selectedData.map(item =>
        this.formatChartLabel(item.time)
      ),

      datasets: [
        {
          label: 'Ride',
          data: selectedData.map(item => item.count),
          borderColor: '#3B82F6',
          backgroundColor: 'rgba(59,130,246,0.1)',
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#3B82F6',
        },
      ],
    };
  }

  getRideAreaChart = () => {

    this.dashboardService
      .getRideAreaChart(this.statisticsParams)
      .subscribe({
        next: (res) => {
          this.rideAreaChart = res.data;
          this.updateRideChartData();
        }
      });
  }

  rideAreaChartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      },
    },
    scales: {
      x: {
        grid: { display: false },
      },
      y: {
        grid: { color: '#E5E7EB' },
        beginAtZero: true,
        ticks: {
          precision: 0
        }
      },
    },
  };

  formatChartLabel = (date: string): string => {

    const currentDate = this.getDate(date);

    if (this.selectedPeriod === 'TODAY') {
      return currentDate.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
    }

    if (this.selectedPeriod === 'THIS_WEEK') {
      return currentDate.toLocaleDateString([], {
        weekday: 'short',
      });
    }

    return currentDate.toLocaleDateString([], {
      day: 'numeric',
      month: 'short',
    });
  }

  redirectToCustomersPage = (ride: Ride) => {
    this.router.navigate(['/customers'], {
      queryParams: { search: ride.customerEmail },
      queryParamsHandling: ''
    });
  }

  getDate(date: string) {
    const [datePart, timePart] = date.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute, second] = timePart.substring(0, 8).split(':').map(Number);

    return new Date(year, month - 1, day, hour, minute, second);
  }

}
